import {
  toDependencyMapDegreesState,
  type DependencyMapDegreesState,
  type DependencyMapViewModel,
  type DependencyMapViewSettings,
  type DependencyMapCoreState,
} from '@ardoq/dependency-map';
import { ViewIds } from '@ardoq/api-types';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import {
  MenuMetaOptions,
  showOnlyConnectedComponents,
  dropdownTraversalMenu,
} from '@ardoq/settings-bar';
import {
  type DropdownConfig,
  type SettingsConfig,
  SettingsType,
  type ToggleConfig,
} from '@ardoq/view-settings';
import { WithUncontrolledPopover } from '@ardoq/popovers';
import { TraversalInfoPopover } from '../view/popovers/traversalInfo/TraversalInfoPopover';
import { colors } from '@ardoq/design-tokens';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import { Features, hasFeature } from '@ardoq/features';
import { isTruthy } from '@ardoq/common-helpers';
import {
  getCollapseChildrenConfig,
  getColorByReferenceConfig,
  includeReferenceParentsSlider,
} from '../settingsControlBuilders';
import { currentDate } from '@ardoq/date-time';
import { IconBadge, StatusType } from '@ardoq/status-ui';
import { t, type I18nLocale } from '@ardoq/i18n';

const VIEW_ID = ViewIds.DEPENDENCY_MAP_2;
export type WorkspaceModeDependencyMapSettingsControlsDependencies =
  DependencyMapDegreesState &
    Pick<
      DependencyMapViewModel,
      | 'traversedOutgoingReferenceTypes'
      | 'traversedIncomingReferenceTypes'
      | 'referenceTypes'
    > & {
      viewSettings: DependencyMapViewSettings;
    };

export const toWorkspaceModeSettingsControlsDependencies = ({
  viewSettings,
  viewModel,
}: DependencyMapCoreState): WorkspaceModeDependencyMapSettingsControlsDependencies => {
  const {
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
    referenceTypes,
  } = viewModel;

  return {
    viewSettings,
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
    referenceTypes,
    ...toDependencyMapDegreesState({ viewSettings, viewModel }),
  };
};

type ToWorkspaceModeSettingsControlsArgs = {
  dependencies: WorkspaceModeDependencyMapSettingsControlsDependencies;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  quickPerspectiveReferenceTypeFilters: Set<string> | null;
  i18nLocale: I18nLocale;
};

export const toWorkspaceModeSettingsControls = ({
  dependencies,
  onViewSettingsUpdate,
  quickPerspectiveReferenceTypeFilters,
  i18nLocale,
}: ToWorkspaceModeSettingsControlsArgs): SettingsConfig[] => {
  const {
    viewSettings,
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
    referenceTypes,
  } = dependencies;
  const { colorByReference, showVertical, seenTraversalInfo } = viewSettings;
  const traversalItem: DropdownConfig = {
    ...dropdownTraversalMenu({
      viewSettings,
      traversedOutgoingReferenceTypes,
      traversedIncomingReferenceTypes,
      referenceTypes,
      viewId: VIEW_ID,
      maximumDepth: 5,
      options: {
        selectParentChildReferenceTypes: false,
        selectReferenceTypes: true,
        degreesOfSingleDirection: true,
        degreesOfBothDirections: false,
        degreesRowAdditionalContent: (
          <WithUncontrolledPopover content={() => <TraversalInfoPopover />}>
            <span style={{ display: 'inline-flex', verticalAlign: 'bottom' }}>
              {seenTraversalInfo === true ? (
                <Icon
                  style={{ marginLeft: '4px' }}
                  iconName={IconName.INFO}
                  iconSize={IconSize.LARGE}
                  color={colors.icon}
                />
              ) : (
                <IconBadge
                  style={{
                    color: colors.icon,
                    marginLeft: '4px',
                  }}
                  iconName={IconName.INFO}
                  color={colors.icon}
                  statusType={StatusType.NEW}
                />
              )}
            </span>
          </WithUncontrolledPopover>
        ),
        metaOptions:
          MenuMetaOptions.ALL_EXCEPT_PARENT_CHILD | MenuMetaOptions.NONE_OPTION,
      },
      onViewSettingsUpdate,
      quickPerspectiveReferenceTypeFilters,
      i18nLocale,
    }),
    onOpened: seenTraversalInfo
      ? undefined
      : () =>
          onViewSettingsUpdate(
            VIEW_ID,
            { seenTraversalInfo: currentDate() },
            true
          ),
  };

  const showVerticalConfig: SettingsConfig = {
    id: 'showVertical',
    label: showVertical
      ? t(i18nLocale, 'tabview:dependencyMap.showHorizontal', 'Show horizontal')
      : t(i18nLocale, 'tabview:dependencyMap.showVertical', 'Show vertical'),
    iconName: showVertical
      ? IconName.ORIENTATION_HORIZONTAL
      : IconName.ORIENTATION_VERTICAL,
    isActive: false,
    currentValue: showVertical,
    type: SettingsType.TOGGLE,
    onViewSettingsUpdate,
  };

  const colorByReferenceConfig: SettingsConfig = getColorByReferenceConfig(
    onViewSettingsUpdate,
    colorByReference,
    i18nLocale
  );

  const collapseChildrenConfig = getCollapseChildrenConfig({
    dependencies,
    onViewSettingsUpdate,
    i18nLocale,
  });

  const includeReferenceParentsConfig = includeReferenceParentsSlider({
    dependencies,
    showChildDegreesSlider: true,
    i18nLocale,
  });
  const useNewGroupingConfig = hasFeature(Features.GROUPING_RESEARCH)
    ? newGrouping(viewSettings, onViewSettingsUpdate, i18nLocale)
    : null;

  return [
    traversalItem,
    collapseChildrenConfig,
    showOnlyConnectedComponents(
      viewSettings.showOnlyConnectedComponents,
      onViewSettingsUpdate
    ),
    showVerticalConfig,
    colorByReferenceConfig,
    includeReferenceParentsConfig,
    useNewGroupingConfig,
  ].filter(isTruthy);
};

const newGrouping = (
  viewSettings: DependencyMapViewSettings,
  onViewSettingsUpdate: OnViewSettingsUpdate,
  i18nLocale: I18nLocale
): ToggleConfig => {
  return {
    id: 'useNewGrouping',
    label: t(
      i18nLocale,
      'tabview:dependencyMap.useNewGrouping',
      'Use new grouping'
    ),
    iconName: IconName.RHOMBUS,
    type: SettingsType.TOGGLE,
    isActive: Boolean(viewSettings.useNewGrouping),
    onViewSettingsUpdate,
  };
};
